import React from "react";
import PropTypes from "prop-types";
import { TextField, FormHelperText, Box } from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import FormikLabel from "./FormLabel";

const TimePickerFieldComponent = ({
  field,
  errors,
  touched,
  values,
  setFieldValue,
}) => {
  const {
    fieldId,
    placeholder,
    disabled,
    dependencies = [], // List of dependent fields
    attribute: { timeFormat = "12", minTime, maxTime } = {},
  } = field;

  const format = timeFormat === "12" ? "hh:mm A" : "HH:mm";
  const [timeValue, setTimeValue] = useState(null);
  const [computedMinTime, setComputedMinTime] = useState(null);
  const [computedMaxTime, setComputedMaxTime] = useState(null);

  useEffect(() => {
    const initialValue = values[fieldId];
    if (initialValue) {
      setTimeValue(dayjs(initialValue, format));
    } else {
      setTimeValue(null);
    }
  }, [values, fieldId, format]);

  useEffect(() => {
    if (dependencies.length > 0) {
      dependencies.forEach((dependency) => {
        const dependentFieldValue = values[dependency.fieldId];
        if (dependentFieldValue) {
          const dependentTime = dayjs(dependentFieldValue, format);

          if (dependency.condType === "minTime") {
            setComputedMinTime(dependentTime);
          } else if (dependency.condType === "maxTime") {
            setComputedMaxTime(dependentTime);
          }
        }
      });
    }
  }, [values, dependencies, format]);

  const handleChange = (newValue, form) => {
    if (dayjs.isDayjs(newValue)) {
      setTimeValue(newValue);
      form.setFieldValue(fieldId, newValue.format(format));
    } else {
      console.warn("Invalid time selected");
      setFieldValue(fieldId, ""); // Reset value if invalid
    }
  };

  const hasError = touched[fieldId] && Boolean(errors[fieldId]);

  // Dynamically parse minTime and maxTime
  const parsedMinTime = minTime ? dayjs(minTime, format) : null;
  const parsedMaxTime = maxTime ? dayjs(maxTime, format) : null;

  return (
    <FormControl fullWidth error={touched[fieldId] && !!errors[fieldId]}>
      <FormikLabel text={fieldName} required={required} />
      <Field name={fieldId}>
        {({ form }) => (
          <TimePicker
            value={timeValue}
            onChange={(newValue) => handleChange(newValue, form)}
            disabled={disabled}
            ampm={timeFormat === "12"}
            minTime={computedMinTime || (minTime ? dayjs(minTime, format) : null)}
            maxTime={computedMaxTime || (maxTime ? dayjs(maxTime, format) : null)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={!timeValue ? placeholder : ""}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: disabled ? "#f5f5f5" : "inherit",
                  },
                }}
              />
            )}
          />
        )}
      </Field>
      {hasError && <FormHelperText>{errors[fieldId]}</FormHelperText>}
    </FormControl>
  );
};

TimePickerFieldComponent.propTypes = {
  field: PropTypes.shape({
    fieldId: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    dependencies: PropTypes.arrayOf(
      PropTypes.shape({
        fieldId: PropTypes.string.isRequired,
        condType: PropTypes.oneOf(["minTime", "maxTime"]).isRequired,
      })
    ),
    attribute: PropTypes.shape({
      timeFormat: PropTypes.string,
      minTime: PropTypes.string,
      maxTime: PropTypes.string,
    }),
  }).isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default TimePickerFieldComponent;
